import React, { useCallback, useEffect, useMemo } from 'react';
import MultiStepModal, { ModalStepT } from 'ui/Modals/MultiStepModal';
import EnvelopeHypothesisModalContent from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/EnvelopeHypothesisModalContent';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { MantyStatus } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { MANTY_STATUS } from 'routes/HumanResourcesModule/business/definitions';
import { orgChartHrFormatter } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { t } from 'i18next';
import NewHypothesisForm from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/components/Modals/NewHypothesisForm';
import { DateString } from 'routes/HumanResourcesModule/business/types';
import {
    getModalTitle,
    useFormState,
} from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/components/Modals/helpers/dataHelpers';
import { useTheme } from '@mui/material';
import { useEnvelopeMantyStatusHypothesisCRUDMutations } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/helpers/hooks/mantyStatusHypothesis';
import { EnvelopeMantyStatusHypothesis } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Envelope/MantyStatusHypothesis/helpers/types';

type Props = {
    open: boolean;
    onClose: () => void;
    hrSimulation: HrSimulation;
    nonRecurrentMantyStatuses: MantyStatus[];
    isUpdateEnabled?: boolean;
    isEditMode?: boolean;
    mantyStatusHypotheses: EnvelopeMantyStatusHypothesis[];
};

const EnvelopeHypothesisBaseModal = ({
    open,
    onClose,
    hrSimulation,
    mantyStatusHypotheses,
    nonRecurrentMantyStatuses,
    isUpdateEnabled = true,
    isEditMode = false,
}: Props) => {
    const theme = useTheme();
    const [formState, setFormState] = useFormState({
        mantyStatus: null,
        valuesByDates: {},
        selectedOrgChartHrsSoftwareId: '',
        comment: '',
        isFormValid: false,
        hasChanges: false,
    });

    const alreadyExistingHypothesis = useMemo(() => {
        if (
            !formState.selectedOrgChartHrsSoftwareId ||
            !formState.mantyStatus
        ) {
            return undefined;
        }
        const existingHypotheses = mantyStatusHypotheses.find(
            (h) =>
                h.orgChartHrSoftwareId ===
                    formState.selectedOrgChartHrsSoftwareId &&
                h.mantyStatus === formState.mantyStatus,
        );
        setFormState((prev) => ({
            ...prev,
        }));
        return existingHypotheses;
    }, [
        mantyStatusHypotheses,
        formState.selectedOrgChartHrsSoftwareId,
        formState.mantyStatus,
    ]);

    useEffect(() => {
        setFormState((prev) => ({
            ...prev,
            comment: alreadyExistingHypothesis?.comment ?? '',
            valuesByDates: alreadyExistingHypothesis?.newValuesByDates ?? {},
        }));
    }, [alreadyExistingHypothesis, setFormState]);
    const { createHypothesis, editHypothesis } =
        useEnvelopeMantyStatusHypothesisCRUDMutations(
            hrSimulation.id,
            formState.selectedOrgChartHrsSoftwareId,
            formState.mantyStatus as MantyStatus,
            alreadyExistingHypothesis?.id,
        );

    const onValidate = () => {
        const { comment, valuesByDates } = formState;
        if (alreadyExistingHypothesis !== undefined) {
            editHypothesis({
                comment,
                valuesByDates,
                oldValuesByDates: alreadyExistingHypothesis.newValuesByDates,
            });
        } else {
            createHypothesis?.({
                comment,
                valuesByDates,
            });
        }
        setFormState({
            mantyStatus: null,
            comment: '',
            valuesByDates: {},
            selectedOrgChartHrsSoftwareId: '',
            isFormValid: false,
            hasChanges: false,
        });
    };
    const isSaveEnabled = useMemo(() => {
        return Boolean(
            formState?.selectedOrgChartHrsSoftwareId && formState?.mantyStatus,
        );
    }, [formState?.selectedOrgChartHrsSoftwareId, formState?.mantyStatus]);

    const existingItem = useMemo(() => {
        return (
            hrSimulation?.orgChartHrs?.find(
                (item) =>
                    item.softwareId ===
                    formState?.selectedOrgChartHrsSoftwareId,
            ) || null
        );
    }, [formState?.selectedOrgChartHrsSoftwareId, hrSimulation?.orgChartHrs]);

    const onValuesByDatesChange = useCallback(
        (newValuesByDates: Record<DateString, number>) => {
            setFormState((prev) => ({
                ...prev,
                valuesByDates: newValuesByDates,
            }));
        },
        [setFormState],
    );

    const setHasChanges = useCallback(() => {
        setFormState((prev) => ({
            ...prev,
            hasChanges: true,
        }));
    }, [setFormState]);

    const setComment = useCallback(
        (comment: string) => {
            setFormState((prev) => ({
                ...prev,
                comment,
            }));
        },
        [setFormState],
    );

    const steps: ModalStepT[] = useMemo(() => {
        const userInputStep = {
            disableClose: true,
            node: (
                <NewHypothesisForm
                    orgChartHrs={hrSimulation.orgChartHrs}
                    formState={formState}
                    setFormState={setFormState}
                    nonRecurrentMantyStatuses={nonRecurrentMantyStatuses}
                />
            ),
            maxWidth: 'xs',
            title: t(
                'humanResources.simulation.envelope.creationModal.userInput',
            ),
            confirmAction: {
                enabled: Boolean(
                    formState.mantyStatus &&
                        formState.selectedOrgChartHrsSoftwareId,
                ),
            },
        };

        const contentStep = {
            disableClose: true,
            stepBackAction: {
                label: t('common.previous'),
            },
            title: getModalTitle(
                formState.mantyStatus,
                existingItem?.name ?? '',
                existingItem?.orgChartHrMapping?.title.toString() ?? '',
            ),
            orgChartHr: orgChartHrFormatter({
                orgChartHrName: existingItem?.name ?? '',
                orgChartHrMapping:
                    existingItem?.orgChartHrMapping?.title.toString() ?? '',
            }),
            node: (
                <EnvelopeHypothesisModalContent
                    hrSimulation={hrSimulation}
                    orgChartHrSoftwareId={
                        formState?.selectedOrgChartHrsSoftwareId ?? ''
                    }
                    isNonRecurrentEnvelope
                    envelopeFieldValue={formState?.mantyStatus ?? ''}
                    envelopeField={MANTY_STATUS}
                    onValuesByDatesChange={onValuesByDatesChange}
                    setHasChanges={setHasChanges}
                    comment={formState?.comment ?? ''}
                    setComment={setComment}
                    commentFieldLabel={t(
                        'humanResources.simulation.agentHypothesis.creationModal.comment',
                    )}
                    hypothesis={alreadyExistingHypothesis}
                    isUpdateEnabled={isUpdateEnabled}
                    translationKey="humanResources.simulation.envelope.creationModal"
                />
            ),
            confirmAction: {
                enabled: formState?.hasChanges && isSaveEnabled,
                label: isEditMode ? 'common.update' : 'common.validate',
                onClick: onValidate,
            },
        };

        return [userInputStep, contentStep];
    }, [
        t,
        formState,
        existingItem,
        onValuesByDatesChange,
        setHasChanges,
        setComment,
        hrSimulation,
        alreadyExistingHypothesis,
        isUpdateEnabled,
        isSaveEnabled,
        onValidate,
    ]);

    return (
        <MultiStepModal
            maxWidth="lg"
            isOpen={open}
            onClose={onClose}
            PaperProps={{
                sx: { backgroundColor: theme.palette.lightGrey },
            }}
            steps={steps}
        />
    );
};

export default EnvelopeHypothesisBaseModal;
